import React, { useState, useEffect } from 'react';
import {
  Plus, Edit, Trash2, Search, MapPin, Monitor,
  HardDrive, Shield, User, Key, Clock, Save, X, Eye, EyeOff, Package, Copy, Filter, Download, QrCode, History, Printer
} from 'lucide-react';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import Modal from './Modal';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import { useLanguage } from '../contexts/LanguageContext';

interface NVR {
  id: number;
  name: string;
  location_id: number;
  location_name: string;
  brand: string;
  model: string;
  ip_address: string;
  inventory_number?: string;
  channel_count: number;
  total_disk_count: number;
  total_disk_space: string;
  disk_sizes: string;
  raid_structure: string;
  usable_space?: string;
  username: string;
  password: string;
  estimated_recording_time: string;
  notes: string;
  created_at: string;
}

interface Location {
  id: number;
  name: string;
}

interface NVRHistory {
  id: number;
  nvr_id: number;
  action: string;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  details?: string;
  user_name: string;
  user_email: string;
  ip_address?: string;
  created_at: string;
}

const maskIpAddress = (ip: string): string => {
  if (!ip) return '';
  const parts = ip.split('.');
  if (parts.length === 4) {
    return `X.X.${parts[2]}.${parts[3]}`;
  }
  return ip;
};

// Disk boyutu seçenekleri
const diskSizeOptions = [
  { value: '1', label: '1 TB' },
  { value: '2', label: '2 TB' },
  { value: '3', label: '3 TB' },
  { value: '4', label: '4 TB' },
  { value: '6', label: '6 TB' },
  { value: '8', label: '8 TB' },
  { value: '10', label: '10 TB' },
  { value: '12', label: '12 TB' },
  { value: '16', label: '16 TB' },
  { value: '20', label: '20 TB' }
];

// RAID tipleri ve açıklamaları
const raidTypes = [
  {
    value: 'RAID 0',
    label: 'RAID 0 (Striping)',
    description: 'Yedeksiz, maksimum performans',
    efficiency: 1.0,
    minDisks: 2
  },
  {
    value: 'RAID 1',
    label: 'RAID 1 (Mirroring)',
    description: 'Tam yedekleme, %50 alan kaybı',
    efficiency: 0.5,
    minDisks: 2
  },
  {
    value: 'RAID 5',
    label: 'RAID 5 (Distributed Parity)',
    description: 'En yaygın, 1 disk hataya tolerans',
    efficiency: 0.75, // (n-1)/n disk for 4+ disks
    minDisks: 3
  },
  {
    value: 'RAID 6',
    label: 'RAID 6 (Double Parity)',
    description: '2 disk hataya tolerans',
    efficiency: 0.67, // (n-2)/n disk for 4+ disks
    minDisks: 4
  },
  {
    value: 'RAID 10',
    label: 'RAID 10 (1+0)',
    description: 'Mirror + Stripe, yüksek performans',
    efficiency: 0.5,
    minDisks: 4
  }
];

const NvrsPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [nvrs, setNvrs] = useState<NVR[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  useGlobalSearch('nvrs', setSearchTerm);
  const [showFilters, setShowFilters] = useState(false);
  const [brandFilter, setBrandFilter] = useState('');
  const [raidFilter, setRaidFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [ipFilter, setIpFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingNvr, setEditingNvr] = useState<NVR | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedNvrHistory, setSelectedNvrHistory] = useState<NVRHistory[]>([]);
  const [selectedNvrForHistory, setSelectedNvrForHistory] = useState<NVR | null>(null);
  
  // Toplu etiket state'leri
  const [showBulkLabelModal, setShowBulkLabelModal] = useState(false);
  const [selectedNvrs, setSelectedNvrs] = useState<number[]>([]);
  const [bulkLabelFilters, setBulkLabelFilters] = useState({
    location: 'all',
    brand: 'all'
  });
  const [showBulkLabelPreview, setShowBulkLabelPreview] = useState(false);
  const [filteredNvrsForLabel, setFilteredNvrsForLabel] = useState<NVR[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    location_id: '',
    brand: '',
    model: '',
    ip_address: '',
    inventory_number: '',
    channel_count: '',
    total_disk_count: '',
    single_disk_size: '',
    total_disk_space: '',
    disk_sizes: '',
    raid_structure: '',
    usable_space: '',
    username: '',
    password: '',
    estimated_recording_time: '',
    notes: ''
  });

  const getRaidDescription = (value: string): string => {
    const map: Record<string, string> = {
      'RAID 0': t('nvrs.raidDesc0'),
      'RAID 1': t('nvrs.raidDesc1'),
      'RAID 5': t('nvrs.raidDesc5'),
      'RAID 6': t('nvrs.raidDesc6'),
      'RAID 10': t('nvrs.raidDesc10'),
    };
    return map[value] || '';
  };

  // Hesaplama fonksiyonları
  const calculateTotalSpace = (diskCount: string, singleDiskSize: string): string => {
    const count = parseInt(diskCount) || 0;
    const size = parseInt(singleDiskSize) || 0;
    return count > 0 && size > 0 ? (count * size).toString() : '';
  };

  const calculateUsableSpace = (totalSpace: string, raidType: string, diskCount: string): string => {
    const total = parseInt(totalSpace) || 0;
    const count = parseInt(diskCount) || 0;

    if (total === 0 || count === 0) return '';

    const raid = raidTypes.find(r => r.value === raidType);
    if (!raid) return '';

    if (count < raid.minDisks) return t('nvrs.minDisks', { n: raid.minDisks });

    let efficiency = raid.efficiency;

    // RAID 5 ve RAID 6 için dinamik hesaplama
    if (raidType === 'RAID 5') {
      efficiency = (count - 1) / count;
    } else if (raidType === 'RAID 6') {
      efficiency = (count - 2) / count;
    }

    const usable = Math.floor(total * efficiency);
    return `${usable} TB`;
  };

  // Hiyerarşik yapı fonksiyonları
  const getLocations = (): string[] => {
    const uniqueLocations = [...new Set(nvrs.map(n => n.location_name).filter(Boolean))];
    return uniqueLocations.sort();
  };

  const getNvrsForLocation = (location: string): NVR[] => {
    return nvrs.filter(n => n.location_name === location);
  };

  const generateNvrName = (locationId: string): string => {
    const location = locations.find(loc => loc.id.toString() === locationId);
    if (!location) return '';

    // Lokasyon adını büyük harfe çevir ve boşlukları kaldır
    const locationPrefix = location.name.toUpperCase().replace(/\s+/g, '-');

    // Bu lokasyonda kaç NVR var sayısını al (basit örnek)
    const existingNvrs = nvrs.filter(nvr => nvr.location_id.toString() === locationId);
    const nextNumber = existingNvrs.length + 1;

    return `${locationPrefix}-NVR${nextNumber}`;
  };

  // Form değişikliklerini dinle ve otomatik hesapla
  useEffect(() => {
    const totalSpace = calculateTotalSpace(formData.total_disk_count, formData.single_disk_size);
    const usableSpace = calculateUsableSpace(totalSpace, formData.raid_structure, formData.total_disk_count);

    setFormData(prev => ({
      ...prev,
      total_disk_space: totalSpace ? `${totalSpace} TB` : '',
      usable_space: usableSpace,
      disk_sizes: formData.single_disk_size ? `${formData.total_disk_count}x${formData.single_disk_size}TB` : ''
    }));
  }, [formData.total_disk_count, formData.single_disk_size, formData.raid_structure]);

  // Lokasyon değiştiğinde NVR adını güncelle
  useEffect(() => {
    if (formData.location_id && !editingNvr) {
      const generatedName = generateNvrName(formData.location_id);
      setFormData(prev => ({ ...prev, name: generatedName }));
    }
  }, [formData.location_id, locations, nvrs, editingNvr]);

  useEffect(() => {
    fetchNvrs();
    fetchLocations();
  }, []);

  // Toplu etiket modal'ı açıldığında filtrelenmiş listeyi başlat
  useEffect(() => {
    if (showBulkLabelModal) {
      setFilteredNvrsForLabel(nvrs);
    }
  }, [showBulkLabelModal, nvrs]);

  // Toplu etiket filtreleri değiştiğinde filtreyi uygula
  useEffect(() => {
    if (showBulkLabelModal) {
      applyBulkLabelFilters();
    }
  }, [bulkLabelFilters, nvrs, showBulkLabelModal]);

  const fetchNvrs = async () => {
    try {
      const response = await fetch('/api/nvrs', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNvrs(data.data || []);
      }
    } catch (error) {
      console.error('NVR\'lar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await fetch('/api/locations', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLocations(data.data || []);
      }
    } catch (error) {
      console.error('Lokasyonlar yüklenemedi:', error);
    }
  };

  const fetchNvrHistory = async (nvrId: number) => {
    try {
      const response = await fetch(`/api/nvrs/${nvrId}/history`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedNvrHistory(data.data || []);
      }
    } catch (error) {
      console.error('NVR geçmiş bilgileri yüklenemedi:', error);
    }
  };

  const showNvrHistory = (nvr: NVR) => {
    setSelectedNvrForHistory(nvr);
    setShowHistoryModal(true);
    fetchNvrHistory(nvr.id);
  };

  // Toplu etiket fonksiyonları
  const applyBulkLabelFilters = () => {
    let filtered = [...nvrs];

    if (bulkLabelFilters.location !== 'all') {
      filtered = filtered.filter(n => n.location_name === bulkLabelFilters.location);
    }

    if (bulkLabelFilters.brand !== 'all') {
      filtered = filtered.filter(n => n.brand === bulkLabelFilters.brand);
    }

    setFilteredNvrsForLabel(filtered);
  };

  const handleNvrSelection = (nvrId: number) => {
    setSelectedNvrs(prev =>
      prev.includes(nvrId)
        ? prev.filter(id => id !== nvrId)
        : [...prev, nvrId]
    );
  };

  const selectAllFiltered = () => {
    const currentList = filteredNvrsForLabel.length > 0 ? filteredNvrsForLabel : nvrs;
    const allIds = currentList.map(n => n.id);
    setSelectedNvrs(allIds);
  };

  const deselectAll = () => {
    setSelectedNvrs([]);
  };

  const generateBulkLabelPreview = () => {
    setShowBulkLabelPreview(true);
  };

  const printBulkLabels = async () => {
    if (selectedNvrs.length === 0) return;

    const selectedNvrData = nvrs.filter(n => selectedNvrs.includes(n.id));
    
    // Her sayfada 4 etiket (2x2) olacak şekilde sayfaları oluştur
    const pages = [];
    for (let i = 0; i < selectedNvrData.length; i += 4) {
      pages.push(selectedNvrData.slice(i, i + 4));
    }

    // Her sayfa için HTML oluştur
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    let htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>NVR Toplu Etiketler</title>
        <style>
          @page { size: A4; margin: 10mm; }
          body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
          .page { width: 100%; height: 100vh; display: flex; flex-wrap: wrap; }
          .label { 
            width: 50%; 
            height: 50%; 
            border: 1px solid #ddd; 
            padding: 10px; 
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
          }
          .qr-code { margin: 5px 0; }
          .nvr-name { font-weight: bold; font-size: 14px; margin: 5px 0; }
          .nvr-details { font-size: 12px; color: #666; }
          @media print {
            .page { page-break-after: always; }
            .page:last-child { page-break-after: avoid; }
          }
        </style>
      </head>
      <body>
    `;

    pages.forEach((page, pageIndex) => {
      htmlContent += `<div class="page">`;
      
      for (let i = 0; i < 4; i++) {
        if (i < page.length) {
          const nvr = page[i];
          htmlContent += `
            <div class="label">
              <div class="qr-code">
                <img src="/api/nvrs/${nvr.id}/qr-code?embed=true&token=${localStorage.getItem('auth_token')}" 
                     alt="QR Code" width="60" height="60" />
              </div>
              <div class="nvr-name">${nvr.name}</div>
              <div class="nvr-details">
                ${nvr.brand} ${nvr.model}<br>
                ${nvr.location_name || t('nvrs.bulkLabelNoLocation')}<br>
                ${nvr.ip_address}
              </div>
            </div>
          `;
        } else {
          htmlContent += `<div class="label"></div>`;
        }
      }
      
      htmlContent += `</div>`;
    });

    htmlContent += `
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    
    // Yazdırma işlemini başlat
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Excel Export
  const exportToExcel = () => {
    const locale = language === 'en' ? 'en-US' : 'tr-TR';
    const data = filteredNvrs.map(nvr => ({
      [t('nvrs.excelName')]: nvr.name,
      [t('nvrs.excelBrand')]: nvr.brand,
      [t('nvrs.excelModel')]: nvr.model,
      [t('nvrs.excelInventory')]: nvr.inventory_number,
      [t('nvrs.excelLocation')]: nvr.location_name,
      [t('nvrs.excelIp')]: nvr.ip_address,
      [t('nvrs.excelChannels')]: nvr.channel_count,
      [t('nvrs.excelDisks')]: nvr.total_disk_count,
      [t('nvrs.excelTotalSpace')]: nvr.total_disk_space,
      [t('nvrs.excelRaid')]: nvr.raid_structure,
      [t('nvrs.excelUsable')]: nvr.usable_space,
      [t('nvrs.excelUsername')]: nvr.username,
      [t('nvrs.excelRecording')]: nvr.estimated_recording_time,
      [t('nvrs.excelNotes')]: nvr.notes,
      [t('nvrs.excelCreatedAt')]: new Date(nvr.created_at).toLocaleDateString(locale)
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'NVRs');
    XLSX.writeFile(wb, `nvrs_${new Date().toLocaleDateString(locale).replace(/\./g, '_')}.xlsx`);
  };

  // QR Kod oluşturma
  const generateQRCode = async (nvrId: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      const siteUrl = import.meta.env.VITE_SITE_URL || `${window.location.protocol}//${window.location.host}`;
      const url = `${siteUrl}/api/nvrs/${nvrId}/qr-code?token=${token}`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('QR kod oluşturma hatası:', error);
    }
  };

  const validateIpAddress = (ip: string): boolean => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // IP adresi format kontrolü
    if (!validateIpAddress(formData.ip_address)) {
      Swal.fire({
        icon: 'error',
        title: t('nvrs.swalIpTitle'),
        text: t('nvrs.swalIpText'),
        confirmButtonText: t('nvrs.swalOk')
      });
      return;
    }

    try {
      const url = editingNvr ? `/api/nvrs/${editingNvr.id}` : '/api/nvrs';
      const method = editingNvr ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchNvrs();
        setShowModal(false);
        resetForm();
        Swal.fire({
          icon: 'success',
          title: t('nvrs.swalSuccessTitle'),
          text: editingNvr ? t('nvrs.swalUpdated') : t('nvrs.swalAdded'),
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        const errorData = await response.json();

        // Değişiklik yoksa özel mesaj göster
        if (errorData.noChanges) {
          Swal.fire({
            icon: 'info',
            title: t('nvrs.swalInfoTitle'),
            text: t('nvrs.swalNoChanges'),
            confirmButtonText: t('nvrs.swalOk')
          });
          return;
        }

        // IP duplicate hatası için özel mesaj
        if (errorData.duplicateIp) {
          Swal.fire({
            icon: 'error',
            title: t('nvrs.swalIpTitle'),
            text: errorData.error,
            confirmButtonText: t('nvrs.swalOk')
          });
          return;
        }

        Swal.fire({
          icon: 'error',
          title: t('nvrs.swalErrorTitle'),
          text: errorData.error || t('nvrs.swalErrorText'),
          confirmButtonText: t('nvrs.swalOk')
        });
      }
    } catch (error) {
      console.error('NVR kaydedilemedi:', error);
      Swal.fire({
        icon: 'error',
        title: t('nvrs.swalErrorTitle'),
        text: t('nvrs.swalErrorText'),
        confirmButtonText: t('nvrs.swalOk')
      });
    }
  };

  const handleEdit = (nvr: NVR) => {
    setEditingNvr(nvr);

    // Mevcut disk_sizes formatından single_disk_size'ı çıkarmaya çalış
    let singleDiskSize = '';
    if (nvr.disk_sizes) {
      const match = nvr.disk_sizes.match(/(\d+)x(\d+)TB/);
      if (match) {
        singleDiskSize = match[2];
      }
    }

    setFormData({
      name: nvr.name,
      location_id: nvr.location_id.toString(),
      brand: nvr.brand,
      model: nvr.model,
      ip_address: nvr.ip_address,
      inventory_number: nvr.inventory_number || '',
      channel_count: nvr.channel_count?.toString() || '',
      total_disk_count: nvr.total_disk_count?.toString() || '',
      single_disk_size: singleDiskSize,
      total_disk_space: nvr.total_disk_space || '',
      disk_sizes: nvr.disk_sizes || '',
      raid_structure: nvr.raid_structure || '',
      usable_space: '',
      username: nvr.username || '',
      password: nvr.password || '',
      estimated_recording_time: nvr.estimated_recording_time || '',
      notes: nvr.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: t('nvrs.swalDeleteTitle'),
      text: t('nvrs.swalDeleteText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('nvrs.swalDeleteConfirm'),
      cancelButtonText: t('nvrs.swalDeleteCancel')
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`/api/nvrs/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        });

        if (response.ok) {
          fetchNvrs();
          Swal.fire({
            icon: 'success',
            title: t('nvrs.swalDeletedTitle'),
            text: t('nvrs.swalDeletedText'),
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          const errorData = await response.json();
          Swal.fire({
            icon: 'error',
            title: t('nvrs.swalErrorTitle'),
            text: errorData.error || t('nvrs.swalDeleteError'),
            confirmButtonText: t('nvrs.swalOk')
          });
        }
      } catch (error) {
        console.error('NVR silinemedi:', error);
        Swal.fire({
          icon: 'error',
          title: t('nvrs.swalErrorTitle'),
          text: t('nvrs.swalErrorText'),
          confirmButtonText: t('nvrs.swalOk')
        });
      }
    }
  };

  const handleCopy = (nvr: NVR) => {
    setEditingNvr(null); // Kopyalama modunda olduğumuzu belirtmek için null yapıyoruz

    // Mevcut disk_sizes formatından single_disk_size'ı çıkarmaya çalış
    let singleDiskSize = '';
    if (nvr.disk_sizes) {
      const match = nvr.disk_sizes.match(/(\d+)x(\d+)TB/);
      if (match) {
        singleDiskSize = match[2];
      }
    }

    setFormData({
      name: '', // Yeni NVR için boş bırakıyoruz, lokasyon seçilince otomatik oluşturulacak
      location_id: '', // Lokasyon seçimi için boş bırakıyoruz
      brand: nvr.brand,
      model: nvr.model,
      ip_address: '', // IP adresini boş bırakıyoruz, kullanıcı yeni bir tane girecek
      inventory_number: '', // Envanter numarasını boş bırakıyoruz
      channel_count: nvr.channel_count?.toString() || '',
      total_disk_count: nvr.total_disk_count?.toString() || '',
      single_disk_size: singleDiskSize,
      total_disk_space: nvr.total_disk_space || '',
      disk_sizes: nvr.disk_sizes || '',
      raid_structure: nvr.raid_structure || '',
      usable_space: '',
      username: nvr.username || '',
      password: nvr.password || '',
      estimated_recording_time: nvr.estimated_recording_time || '',
      notes: nvr.notes || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '', location_id: '', brand: '', model: '', ip_address: '', inventory_number: '',
      channel_count: '', total_disk_count: '', single_disk_size: '',
      total_disk_space: '', disk_sizes: '', raid_structure: '', usable_space: '',
      username: '', password: '', estimated_recording_time: '', notes: ''
    });
    setEditingNvr(null);
    setShowPassword(false);
  };

  // Seçili lokasyonun NVR'larını filtrele
  const filteredNvrs = (selectedLocation ? getNvrsForLocation(selectedLocation) : []).filter(nvr => {
    // Gelişmiş arama - tüm alanlarda arama yap
    const matchesSearch = !searchTerm || (
      String(nvr.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(nvr.brand || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(nvr.model || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(nvr.location_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(nvr.inventory_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(nvr.ip_address || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(nvr.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(nvr.raid_structure || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(nvr.notes || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Marka filtresi
    const matchesBrand = !brandFilter || nvr.brand === brandFilter;

    // RAID filtresi
    const matchesRaid = !raidFilter || nvr.raid_structure === raidFilter;

    // IP filtresi - daha detaylı IP arama
    const matchesIp = !ipFilter || (
      String(nvr.ip_address || '').toLowerCase().includes(ipFilter.toLowerCase()) ||
      String(nvr.ip_address || '').replace(/\./g, '').includes(ipFilter.replace(/\./g, ''))
    );

    return matchesSearch && matchesBrand && matchesRaid && matchesIp;
  });

  // Pagination calculations
  const totalItems = filteredNvrs.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedNvrs = filteredNvrs.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, brandFilter, raidFilter, ipFilter, selectedLocation]);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('nvrs.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">{t('nvrs.subtitle')}</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              showFilters ? 'bg-purple-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
            }`}
          >
            <Filter className="w-4 h-4 mr-2" />
            {t('nvrs.filter')}
          </button>
          <button
            onClick={() => setShowBulkLabelModal(true)}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <QrCode className="w-4 h-4 mr-2" />
            {t('nvrs.bulkLabel')}
          </button>
          <button
            onClick={exportToExcel}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Excel
          </button>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('nvrs.newNvr')}
          </button>
        </div>
      </div>

      {/* Arama */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={t('nvrs.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      {/* Lokasyon Kartları */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
          <MapPin className="w-5 h-5 mr-2" />
          {t('nvrs.selectLocations')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {getLocations().map((location) => {
            const nvrCount = getNvrsForLocation(location).length;
            const isSelected = selectedLocation === location;
            return (
              <button
                key={location}
                onClick={() => {
                  setSelectedLocation(isSelected ? null : location);
                  setCurrentPage(1);
                }}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  isSelected
                    ? 'border-primary bg-primary/10 dark:bg-primary/20'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary/50 dark:hover:border-primary'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className={`font-semibold ${isSelected ? 'text-primary dark:text-primary' : 'text-gray-900 dark:text-gray-100'}`}>
                      {location}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {t('nvrs.nvrCount', { count: nvrCount })}
                    </p>
                  </div>
                  {isSelected && (
                    <div className="ml-2">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <span className="text-white text-sm">✓</span>
                      </div>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filtreler - Seçili lokasyon varsa göster */}
      {selectedLocation && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            {t('nvrs.filterTitle', { location: selectedLocation })}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('nvrs.filterBrand')}
              </label>
              <select
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              >
                <option value="">{t('nvrs.allBrands')}</option>
                {[...new Set(getNvrsForLocation(selectedLocation).map(n => n.brand).filter(Boolean))].map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('nvrs.filterIp')}
              </label>
              <input
                type="text"
                placeholder={t('nvrs.filterIpPlaceholder')}
                value={ipFilter}
                onChange={(e) => setIpFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('nvrs.filterRaid')}
              </label>
              <select
                value={raidFilter}
                onChange={(e) => setRaidFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              >
                <option value="">{t('nvrs.allRaidTypes')}</option>
                {[...new Set(getNvrsForLocation(selectedLocation).map(n => n.raid_structure).filter(Boolean))].map(raid => (
                  <option key={raid} value={raid}>{raid}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* NVR Listesi */}
      {!selectedLocation && (
        <div className="bg-primary/10 dark:bg-primary/20 border border-primary/30 dark:border-primary-dark rounded-lg p-6 text-center">
          <MapPin className="w-12 h-12 text-primary dark:text-primary mx-auto mb-4" />
          <p className="text-gray-700 dark:text-gray-300 text-lg">
            {t('nvrs.selectLocationPrompt')}
          </p>
        </div>
      )}

      {selectedLocation && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">{t('nvrs.colInfo')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">{t('nvrs.colTech')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">{t('nvrs.colStorage')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">{t('nvrs.colActions')}</th>
                </tr>
              </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedNvrs.map((nvr) => (
                <tr key={nvr.id} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{nvr.name}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{nvr.brand} {nvr.model}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{maskIpAddress(nvr.ip_address)}</div>
                      {nvr.inventory_number && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center mt-1">
                          <Package className="w-3 h-3 mr-1" />
                          {nvr.inventory_number}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      <div className="flex items-center mb-1">
                        <Monitor className="w-3 h-3 mr-1" />
                        {t('nvrs.channels', { count: nvr.channel_count })}
                      </div>
                      <div className="flex items-center">
                        <User className="w-3 h-3 mr-1" />
                        {nvr.username}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      <div className="flex items-center mb-1">
                        <HardDrive className="w-3 h-3 mr-1" />
                        {nvr.total_disk_count} Disk - {nvr.total_disk_space}
                      </div>
                      <div className="flex items-center mb-1">
                        <Shield className="w-3 h-3 mr-1" />
                        {nvr.raid_structure}
                      </div>
                      <div className="flex items-center mb-1">
                        <Clock className="w-3 h-3 mr-1" />
                        {nvr.estimated_recording_time}
                      </div>
                      {nvr.usable_space && (
                        <div className="flex items-center text-green-600 dark:text-green-400">
                          <HardDrive className="w-3 h-3 mr-1" />
                          {t('nvrs.usable', { space: nvr.usable_space })}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(nvr)}
                        className="text-primary hover:text-primary-dark dark:text-primary"
                        title={t('nvrs.titleEdit')}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleCopy(nvr)}
                        className="text-green-600 hover:text-green-900 dark:text-green-400"
                        title={t('nvrs.titleCopy')}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => generateQRCode(nvr.id)}
                        className="text-purple-600 hover:text-purple-900 dark:text-purple-400"
                        title={t('nvrs.titleQr')}
                      >
                        <QrCode className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => showNvrHistory(nvr)}
                        className="text-orange-600 hover:text-orange-900 dark:text-orange-400"
                        title={t('nvrs.titleHistory')}
                      >
                        <History className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(nvr.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400"
                        title={t('nvrs.titleDelete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('nvrs.prevPage')}
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('nvrs.nextPage')}
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('nvrs.paginationInfo', { start: startIndex + 1, end: Math.min(endIndex, totalItems), total: totalItems })}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ‹
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-primary/10 dark:bg-primary/90 border-primary text-primary dark:text-accent'
                            : 'bg-white dark:bg-gray-800 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ›
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
        </div>
      )}

      {/* Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {editingNvr ? t('nvrs.modalEditTitle') : (formData.brand && formData.model ? t('nvrs.modalCopyTitle') : t('nvrs.modalNewTitle'))}
              </h2>
              <button onClick={() => setShowModal(false)}>
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nvrs.labelName')}</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    readOnly={editingNvr ? false : !formData.brand} // Kopyalama modunda düzenlenebilir
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder={editingNvr ? "" : t('nvrs.placeholderAutoName')}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white ${editingNvr ? '' : (!formData.brand ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : '')}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nvrs.labelLocation')}</label>
                  <select
                    required
                    value={formData.location_id}
                    onChange={(e) => setFormData({...formData, location_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('nvrs.placeholderSelectLocation')}</option>
                    {locations.map(location => (
                      <option key={location.id} value={location.id}>{location.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nvrs.labelBrand')}</label>
                  <input
                    type="text"
                    required
                    value={formData.brand}
                    onChange={(e) => setFormData({...formData, brand: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nvrs.labelModel')}</label>
                  <input
                    type="text"
                    required
                    value={formData.model}
                    onChange={(e) => setFormData({...formData, model: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nvrs.labelIp')}</label>
                  <input
                    type="text"
                    required
                    value={formData.ip_address}
                    onChange={(e) => setFormData({...formData, ip_address: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nvrs.labelInventory')}</label>
                  <input
                    type="text"
                    value={formData.inventory_number}
                    onChange={(e) => setFormData({...formData, inventory_number: e.target.value})}
                    placeholder="ENV-NVR-001"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nvrs.labelChannels')}</label>
                  <input
                    type="number"
                    value={formData.channel_count}
                    onChange={(e) => setFormData({...formData, channel_count: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nvrs.labelDiskCount')}</label>
                  <input
                    type="number"
                    value={formData.total_disk_count}
                    onChange={(e) => setFormData({...formData, total_disk_count: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nvrs.labelDiskSize')}</label>
                  <select
                    required
                    value={formData.single_disk_size}
                    onChange={(e) => setFormData({...formData, single_disk_size: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('nvrs.placeholderSelectDisk')}</option>
                    {diskSizeOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nvrs.labelTotalSpace')}</label>
                  <input
                    type="text"
                    value={formData.total_disk_space}
                    readOnly
                    placeholder={t('nvrs.placeholderAutoCalc')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nvrs.labelRaid')}</label>
                  <select
                    required
                    value={formData.raid_structure}
                    onChange={(e) => setFormData({...formData, raid_structure: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('nvrs.placeholderSelectRaid')}</option>
                    {raidTypes.map(raid => (
                      <option key={raid.value} value={raid.value}>
                        {raid.label} - {getRaidDescription(raid.value)}
                      </option>
                    ))}
                  </select>
                  {formData.raid_structure && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {getRaidDescription(formData.raid_structure)}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nvrs.labelUsable')}</label>
                  <input
                    type="text"
                    value={formData.usable_space}
                    readOnly
                    placeholder={t('nvrs.placeholderRaidCalc')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nvrs.labelUsername')}</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nvrs.labelPassword')}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nvrs.labelRecordingTime')}</label>
                  <input
                    type="text"
                    value={formData.estimated_recording_time}
                    onChange={(e) => setFormData({...formData, estimated_recording_time: e.target.value})}
                    placeholder={t('nvrs.placeholderRecordingTime')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nvrs.labelNotes')}</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  {t('nvrs.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {t('nvrs.save')}
                </button>
              </div>
            </form>
          </div>
        </Modal>

      {/* NVR Geçmiş Bilgisi Modal */}
      <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)}>
          {selectedNvrForHistory && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {t('nvrs.historyTitle')}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {selectedNvrForHistory.name} - {selectedNvrForHistory.brand} {selectedNvrForHistory.model}
                </p>
              </div>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {selectedNvrHistory.length === 0 ? (
                <div className="text-center py-8">
                  <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">{t('nvrs.historyEmpty')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedNvrHistory.map((history) => (
                    <div key={history.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              history.action === 'CREATE' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                              history.action === 'UPDATE' ? 'bg-primary/20 text-primary-dark dark:bg-primary/90 dark:text-accent' :
                              history.action === 'DELETE' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                              'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                            }`}>
                              {history.action === 'CREATE' ? t('nvrs.historyCreated') :
                               history.action === 'UPDATE' ? t('nvrs.historyUpdated') :
                               history.action === 'DELETE' ? t('nvrs.historyDeleted') : history.action}
                            </span>
                            {history.field_name && (
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {t('nvrs.historyField', { field: history.field_name })}
                              </span>
                            )}
                          </div>
                          
                          {history.details && (
                            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                              {history.details}
                            </p>
                          )}

                          {history.field_name && history.old_value !== null && history.new_value !== null && (
                            <div className="text-sm">
                              <div className="flex items-center space-x-2">
                                <span className="text-red-600 dark:text-red-400 line-through">
                                  {history.old_value || t('nvrs.historyEmptyValue')}
                                </span>
                                <span className="text-gray-400">→</span>
                                <span className="text-green-600 dark:text-green-400">
                                  {history.new_value || t('nvrs.historyEmptyValue')}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="text-right text-sm text-gray-500 dark:text-gray-400 ml-4">
                          <div>{history.user_name}</div>
                          <div>{new Date(history.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}</div>
                          {history.ip_address && (
                            <div className="text-xs">{history.ip_address}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}
        </Modal>

      {/* Toplu Etiket Modal */}
      <Modal isOpen={showBulkLabelModal} onClose={() => setShowBulkLabelModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <QrCode className="h-6 w-6" />
                {t('nvrs.bulkLabelTitle')}
              </h2>
              <button
                onClick={() => {
                  setShowBulkLabelModal(false);
                  setSelectedNvrs([]);
                  setShowBulkLabelPreview(false);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {!showBulkLabelPreview ? (
                <>
                  {/* Filtreler */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                      {t('nvrs.bulkLabelFilters')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('nvrs.bulkLabelLocation')}
                        </label>
                        <select
                          value={bulkLabelFilters.location}
                          onChange={(e) => setBulkLabelFilters({...bulkLabelFilters, location: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="all">{t('nvrs.bulkLabelAllLocations')}</option>
                          {[...new Set(nvrs.map(n => n.location_name).filter(Boolean))].map(location => (
                            <option key={location} value={location}>{location}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('nvrs.bulkLabelBrand')}
                        </label>
                        <select
                          value={bulkLabelFilters.brand}
                          onChange={(e) => setBulkLabelFilters({...bulkLabelFilters, brand: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="all">{t('nvrs.bulkLabelAllBrands')}</option>
                          {[...new Set(nvrs.map(n => n.brand).filter(Boolean))].map(brand => (
                            <option key={brand} value={brand}>{brand}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="mt-4">
                      <button
                        onClick={applyBulkLabelFilters}
                        className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2"
                      >
                        <Search className="h-4 w-4" />
                        {t('nvrs.bulkLabelFilter')}
                      </button>
                    </div>
                  </div>

                  {/* NVR Seçimi */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {t('nvrs.bulkLabelSelection', { count: selectedNvrs.length })}
                      </h3>
                      <div className="flex gap-2">
                        <button
                          onClick={selectAllFiltered}
                          className="text-sm bg-primary text-white px-3 py-1 rounded hover:bg-primary-dark"
                        >
                          {t('nvrs.bulkLabelSelectAll')}
                        </button>
                        <button
                          onClick={deselectAll}
                          className="text-sm bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700"
                        >
                          {t('nvrs.bulkLabelDeselectAll')}
                        </button>
                      </div>
                    </div>

                    <div className="max-h-64 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg">
                      <div className="grid grid-cols-1 gap-2 p-4">
                        {(filteredNvrsForLabel.length > 0 ? filteredNvrsForLabel : nvrs).map(nvr => (
                          <label
                            key={nvr.id}
                            className="flex items-center space-x-3 p-3 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 rounded-lg cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedNvrs.includes(nvr.id)}
                              onChange={() => handleNvrSelection(nvr.id)}
                              className="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {nvr.name}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {nvr.brand} {nvr.model} - {nvr.location_name || t('nvrs.bulkLabelNoLocation')} - {nvr.ip_address}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowBulkLabelModal(false);
                        setSelectedNvrs([]);
                      }}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                    >
                      {t('nvrs.cancel')}
                    </button>
                    <button
                      onClick={generateBulkLabelPreview}
                      disabled={selectedNvrs.length === 0}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      {t('nvrs.bulkLabelPreview', { count: selectedNvrs.length })}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Preview */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                      {t('nvrs.bulkLabelPreviewTitle', { count: selectedNvrs.length, pages: Math.ceil(selectedNvrs.length / 4) })}
                    </h3>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {t('nvrs.bulkLabelPerPage')}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowBulkLabelPreview(false)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                    >
                      {t('nvrs.bulkLabelBack')}
                    </button>
                    <button
                      onClick={printBulkLabels}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                    >
                      <Printer className="h-4 w-4" />
                      {t('nvrs.bulkLabelPrint')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Modal>
    </div>
  );
};

export default NvrsPage;